import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { TagTableSkeletonDesktop, TagTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { BUTTONS, NO_SEARCH_RESULTS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import { MobileDateField, MobileLabelValue } from "@/components/cards/MobileDateField";
import DeleteTagModal from "@/components/modals/DeleteTagModal";
import EditTagModal from "@/components/modals/EditTagModal";
import TagColorMenuItem, { TagColorPickerFloating, type TagColorPickerState } from "@/components/overlays/TagColorOverlay";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";

interface TagTableProps {
    tags: any;
    loadingData: boolean;
    setTags: any;
    dataLength: number;
    sorting: string;
    isLoading: boolean;
    allFetched?: boolean;
    setSorting: ({ }) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
}

function TagRowActions({
    tag,
    rowKey,
    tableId,
    onEdit,
    onDelete,
    onOpenColorPicker,
}: {
    tag: { id: string | number; name?: string; color?: string };
    rowKey: number;
    tableId: string;
    onEdit: (tag: { id?: string | number; name?: string }) => void;
    onDelete: (tag: { id?: string | number; name?: string }) => void;
    onOpenColorPicker: (state: TagColorPickerState) => void;
}) {
    return (
        <Dropdown as={ButtonGroup} size="sm" className="gs-tag-split-btn" drop="down-centered" align="end">
            <Button
                variant="light"
                className="gs-tag-split-btn__main"
                id={`${tableId}-edit-${rowKey}`}
                onClick={() => onEdit(tag)}
            >
                {BUTTONS.EDIT}
            </Button>
            <Dropdown.Toggle
                split
                variant="light"
                className="gs-tag-split-btn__toggle"
                id={`${tableId}-toggle-${rowKey}`}
            />
            <Dropdown.Menu className="gs-tag-dropdown-content">
                <TagColorMenuItem
                    tag={tag}
                    id={`${tableId}-tag-dropdown-color-option-${rowKey}`}
                    onOpen={onOpenColorPicker}
                />
                <Dropdown.Item
                    id={`${tableId}-delete-${rowKey}`}
                    onClick={() => onDelete(tag)}
                >
                    {BUTTONS.DELETE}
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
}

function TagTable(props: TagTableProps) {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [subjectStatus, setSubjectStatus] = useState(true);
    const [updatedAtStatus, setUpdatedAtStatus] = useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [selectedTag, setSelectedTag] = React.useState<{ id?: string | number; name?: string } | undefined>(undefined);
    const [colorPicker, setColorPicker] = React.useState<TagColorPickerState | null>(null);

    const openEditModal = (tag: { id?: string | number; name?: string }) => {
        setSelectedTag(tag);
        setShowEditModal(true);
    };

    const openDeleteModal = (tag: { id?: string | number; name?: string }) => {
        setSelectedTag(tag);
        setShowDeleteModal(true);
    };

    const lastChangeSort = () => {
        setUpdatedAtStatus(!updatedAtStatus);
        setSortElement("updated_at");
        props.setPageNo(1);
        props.setSorting({ updated_at: !updatedAtStatus ? 1 : -1 });
    };

    return (
        <>
            {
                isCompactLayout ? (
                    props.tags && !props.dataLoad ? (
                        props.tags?.length > 0 ? (
                            <div className="tag_table">
                                {props.tags?.map((tag: any, key: number) => (
                                    <div className="envelope_tr gs-mobile-list-card" key={key}>
                                        <div className="gs-mobile-card-icon">
                                            <em className="icon ni ni-tags icons" id={`${props.id}-icon`}></em>
                                        </div>
                                        <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                            <div className="text-dark">
                                                <span className="fw-bold">{TABLE_HEADERS.NAME}: </span>
                                                <span className="tb-lead-sub" id={`${props.id}-created-by-${key}-mb`}>
                                                    {tag.name}
                                                </span>
                                            </div>
                                            <MobileLabelValue
                                                label={`${TABLE_HEADERS.USAGE_COUNT}: `}
                                                value={tag?.usage_count ? tag?.usage_count : "0"}
                                            />
                                            <MobileLabelValue
                                                label={`${TABLE_HEADERS.SENDER}: `}
                                                value={concatenateName(tag.user?.first_name, tag.user?.last_name)}
                                            />
                                            <MobileDateField
                                                label="Created At: "
                                                value={tag.created_at}
                                                dateId={`${props.id}-created-at-date-${key}-mb`}
                                                timeId={`${props.id}-created-at-time-${key}-mb`}
                                            />
                                            <div className="text-dark pt-1">
                                                <TagRowActions
                                                    tag={tag}
                                                    rowKey={key}
                                                    tableId={props.id}
                                                    onEdit={openEditModal}
                                                    onDelete={openDeleteModal}
                                                    onOpenColorPicker={setColorPicker}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div >
                        ) : props.searchItem ? (
                            <NoDataAvailable>
                                <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                            </NoDataAvailable>
                        ) : (
                            <NoDataAvailable>
                                <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                            </NoDataAvailable>
                        )
                    ) : (
                        <TagTableSkeletonMobile />
                    )
                ) : (
                    <table className="gs-tag-table nk-tb-list nk-tb-ulist table-weight" style={{ tableLayout: "fixed", width: "100%" }}>
                        <thead>
                            <tr className="nk-tb-item nk-tb-head">
                                <th className="nk-tb-col gs-tag-col-name">
                                    <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                                </th>
                                <th className="nk-tb-col gs-tag-col-usage">
                                    <span id={`${props.id}-usage-count-th`}>{TABLE_HEADERS.USAGE_COUNT}</span>
                                </th>
                                <th className="nk-tb-col gs-tag-col-created-by">
                                    <span id={`${props.id}-created-by-th`}>{TABLE_HEADERS.CREATED_BY}</span>
                                </th>
                                <th className="nk-tb-col gs-tag-col-created-at">
                                    <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                                </th>
                                <th className="nk-tb-col gs-tag-col-actions" aria-hidden="true" />
                            </tr>
                        </thead>
                        {props.tags && !props.dataLoad ? (
                            props.tags?.length > 0 ? (
                                <tbody>
                                    {props.tags.map((tag: any, key: number) => (
                                        <tr className="nk-tb-item" key={key}>
                                            <td className="nk-tb-col gs-tag-col-name subject-col">
                                                <div className="project-title align-middle d-flex align-items-center">
                                                    <div className="icon mr-2 fs-5 mt-1">
                                                        <em
                                                            id={`sidebar-tag-${key}-icon`}
                                                            className="icon ni ni-label-fill"
                                                            style={{
                                                                fontSize: "20px",
                                                                transform: 'rotateY(-180deg)',
                                                                color: tag.color || ''
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="info min-w-0">
                                                        <span className="tb-lead name">{tag.name}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="nk-tb-col gs-tag-col-usage tb-col-mb">
                                                <span className="text-dark">{tag?.usage_count ? tag?.usage_count : "0"}</span>
                                            </td>
                                            <td className="nk-tb-col gs-tag-col-created-by tb-col-mb">
                                                <span className="text-dark">{concatenateName(tag?.user?.first_name, tag?.user?.last_name)}</span>
                                            </td>
                                            <td className="nk-tb-col gs-tag-col-created-at tb-col-mb">
                                                <span className="text-dark">{DateFormatter(tag.created_at)}</span>
                                                <div className="meta mt-1">
                                                    <span className="text-soft">{TimeFormatter(tag.created_at)}</span>
                                                </div>
                                            </td>
                                            <td className="nk-tb-col gs-tag-col-actions gs-tag-table-actions">
                                                <TagRowActions
                                                    tag={tag}
                                                    rowKey={key}
                                                    tableId={props.id}
                                                    onEdit={openEditModal}
                                                    onDelete={openDeleteModal}
                                                    onOpenColorPicker={setColorPicker}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    {props.isLoading && <TagTableSkeletonDesktop rowCount={2} />}
                                </tbody>
                            ) : props.searchItem ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={5}>
                                            <NoDataAvailable>
                                                <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                            </NoDataAvailable>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    <tr>
                                        <td colSpan={5}>
                                            <NoDataAvailable>
                                                <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                            </NoDataAvailable>
                                        </td>
                                    </tr>
                                </tbody>
                            )
                        ) : (
                            <tbody>
                                <TagTableSkeletonDesktop />
                            </tbody>
                        )}
                    </table>
                )
            }

            <DeleteTagModal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} selectedTag={selectedTag} />
            <EditTagModal show={showEditModal} onHide={() => setShowEditModal(false)} selectedTag={selectedTag} />
            {colorPicker ? (
                <TagColorPickerFloating picker={colorPicker} onClose={() => setColorPicker(null)} />
            ) : null}
        </>
    );
}

export default TagTable;
